import React, { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';

export const TiptapEditorExtensions = [
    StarterKit,
    Superscript,
    Subscript,
    TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
    }),
    Image.configure({
        allowBase64: true,
    }),
];

export type EditorWithIdType = {
    editor: Editor | null;
    textBlockId: string | null;
};

export enum ACTION_TYPE {
    ADD_EDITOR = 'ADD_EDITOR',
    SET_EDITOR_BEING_EDITED = 'SET_EDITOR_BEING_EDITED',
}

export type TextBlockDataState = {
    editors: EditorWithIdType[] | null;
    editorBeingEdited: EditorWithIdType | null;
};

export type AddEditor = {
    type: ACTION_TYPE.ADD_EDITOR;
    payload: EditorWithIdType;
};

export type SetEditorBeingEdited = {
    type: ACTION_TYPE.SET_EDITOR_BEING_EDITED;
    payload: EditorWithIdType | null;
};

type Actions = AddEditor | SetEditorBeingEdited;

export const initialState: TextBlockDataState = {
    editors: null,
    editorBeingEdited: null,
};

export const useTextBlockStateReducer = (state: TextBlockDataState, action: Actions): TextBlockDataState => {
    switch (action.type) {
        case ACTION_TYPE.ADD_EDITOR: {
            const others = (state.editors ?? []).filter((e) => e.textBlockId !== action.payload.textBlockId);
            return { ...state, editors: [...others, action.payload] };
        }
        case ACTION_TYPE.SET_EDITOR_BEING_EDITED: {
            return { ...state, editorBeingEdited: action.payload };
        }
        default:
            return state;
    }
};

type TextBlockStateDispatch = {
    addEditor: (editorPayload: EditorWithIdType) => void;
    setEditorBeingEdited: (textBlockId: string | null) => void;
};

export type TextBlockProviderProps = {
    children: ReactNode;
};

export const TextBlockDataStateDispatchContext = createContext({} as TextBlockStateDispatch);
const TextBlockDataStateContext = createContext({} as TextBlockDataState);

const dispatchTextBlockData = (): TextBlockStateDispatch => useContext(TextBlockDataStateDispatchContext);
const useTextBlockData = (): TextBlockDataState => useContext(TextBlockDataStateContext);

const TextBlockDataProvider = (props: TextBlockProviderProps) => {
    const { children } = props;
    const [state, dispatch] = useReducer(useTextBlockStateReducer, initialState);

    const addEditor = React.useCallback((editorPayload: EditorWithIdType) => {
        dispatch({ type: ACTION_TYPE.ADD_EDITOR, payload: editorPayload });
    }, []);

    const setEditorBeingEdited = useCallback(
        (textBlockId: string | null) => {
            const editorWithId = state.editors?.find((e) => e.textBlockId === textBlockId) ?? null;
            dispatch({ type: ACTION_TYPE.SET_EDITOR_BEING_EDITED, payload: editorWithId });
        },
        [state.editors]
    );

    const dispatchValue = useMemo(() => ({ addEditor, setEditorBeingEdited }), [addEditor, setEditorBeingEdited]);

    return (
        <TextBlockDataStateContext.Provider value={state}>
            <TextBlockDataStateDispatchContext.Provider value={dispatchValue}>
                {children}
            </TextBlockDataStateDispatchContext.Provider>
        </TextBlockDataStateContext.Provider>
    );
};

export { TextBlockDataProvider, dispatchTextBlockData, useTextBlockData };
